import { useEffect, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { currentUser, initIdentity, onAuthChange, openLogin, logout, type IdentityUser } from "./lib/identity";
import { ProjectProvider } from "./lib/ProjectContext";
import { useCurrentAppUser } from "./lib/useCurrentAppUser";
import { OfflineBanner } from "./components/OfflineBanner";
import { Rail } from "./components/Rail";
import { Topbar } from "./components/Topbar";
import { DashboardPage } from "./pages/DashboardPage";
import { AskPage } from "./pages/AskPage";
import { ChatHistoryPage } from "./pages/ChatHistoryPage";
import { SourcesPage } from "./pages/SourcesPage";
import { ContractDocsPage } from "./pages/ContractDocsPage";
import { EscalationsPage } from "./pages/EscalationsPage";
import { NetworkPage } from "./pages/NetworkPage";
import { ProfilePage } from "./pages/ProfilePage";
import { MessagesPage } from "./pages/MessagesPage";
import { MessageThreadPage } from "./pages/MessageThreadPage";
import { AdminUploadPage } from "./pages/AdminUploadPage";
import { ReviewPage } from "./pages/ReviewPage";
import { NewProjectPage } from "./pages/NewProjectPage";
import { SettingsPage } from "./pages/SettingsPage";
import { AdminUsersPage } from "./pages/AdminUsersPage";
import { AnswerQualityPage } from "./pages/AnswerQualityPage";

function LoggedOutScreen() {
  return (
    <div className="center-screen">
      <div className="rail-mark" aria-hidden="true" />
      <h1>CEI Nexus</h1>
      <p className="muted" style={{ maxWidth: 520 }}>
        Construction intelligence for the field. Ask project questions, verify the controlling source, and move from plans to answers with traceable evidence.
      </p>
      <button className="btn btn-primary" onClick={openLogin}>Sign in</button>
    </div>
  );
}

function BlockedAccessScreen({ message }: { message: string }) {
  return (
    <div className="center-screen">
      <div className="rail-mark" aria-hidden="true" />
      <h1>CEI Nexus</h1>
      <p className="muted" style={{ maxWidth: 420 }}>{message}</p>
      <button className="btn" onClick={logout}>Log out</button>
    </div>
  );
}

function AuthedApp({ user }: { user: IdentityUser }) {
  const { user: appUser, role, loading, error, refresh } = useCurrentAppUser();

  useEffect(() => {
    if (error && error.status !== 403) logout();
  }, [error]);

  if (loading) return null;
  if (error?.status === 403) return <BlockedAccessScreen message={error.message} />;
  if (error) return null;

  return (
    <ProjectProvider>
      <div className="shell">
        <Rail user={user} appUser={appUser} role={role} onLogout={logout} />
        <div className="main">
          <OfflineBanner />
          <Topbar />
          <main className="content">
            <Routes>
              <Route path="/" element={<DashboardPage />} />
              <Route path="/ask" element={<AskPage user={user} />} />
              <Route path="/chats" element={<ChatHistoryPage />} />
              <Route path="/sources" element={<SourcesPage />} />
              <Route path="/contract-docs" element={<ContractDocsPage />} />
              <Route path="/escalations" element={<EscalationsPage />} />
              <Route path="/network" element={<NetworkPage />} />
              <Route path="/network/:userId" element={<ProfilePage />} />
              <Route path="/messages" element={<MessagesPage />} />
              <Route path="/messages/:userId" element={<MessageThreadPage />} />
              <Route path="/upload" element={<AdminUploadPage />} />
              <Route path="/review" element={<ReviewPage />} />
              <Route path="/projects/new" element={<NewProjectPage />} />
              <Route path="/settings" element={<SettingsPage user={user} role={role} onAvatarChange={refresh} />} />
              <Route path="/admin/users" element={role === "owner" ? <AdminUsersPage /> : <Navigate to="/" replace />} />
              <Route path="/admin/answer-quality" element={role === "owner" ? <AnswerQualityPage /> : <Navigate to="/" replace />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>
        </div>
      </div>
    </ProjectProvider>
  );
}

export default function App() {
  const [user, setUser] = useState<IdentityUser | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    initIdentity();
    setUser(currentUser());
    setReady(true);
    return onAuthChange(() => setUser(currentUser()));
  }, []);

  if (!ready) return null;
  return user ? <AuthedApp user={user} /> : <LoggedOutScreen />;
}
